/**
 * Blocks PostHog's debug toolbar from being activated by a crafted link by blocking it
 * in the URL fragment, in the persisted payload, and at script load.
 */

export const TOOLBAR_PARAMS_STORAGE_KEY = "_postHogToolbarParams";
export const TOOLBAR_SCRIPT_PATTERN = /^\/static\/(?:[^/]+\/)?toolbar\.js$/;

const ACTIVATION_PATTERNS = [/__posthog=[^&]*/g, /state=[^&]*/g];

function stripHash(hash: string): string | null {
  const original = hash.replace(/^#/, "");
  let remaining = original;
  for (;;) {
    const stripped = ACTIVATION_PATTERNS.reduce(
      (acc, pattern) => acc.replace(pattern, ""),
      remaining,
    );
    if (stripped === remaining) break;
    remaining = stripped.replace(/&{2,}/g, "&").replace(/^&|&$/g, "");
  }

  return remaining === original ? null : remaining;
}

export function purgeToolbarParams(): void {
  try {
    window.localStorage.removeItem(TOOLBAR_PARAMS_STORAGE_KEY);
  } catch {
    // nothing to remove
  }
}

export function blockToolbarScript(script: HTMLScriptElement): HTMLScriptElement | null {
  let pathname: string;
  try {
    pathname = new URL(script.src, window.location.href).pathname;
  } catch {
    return script;
  }

  if (!TOOLBAR_SCRIPT_PATTERN.test(pathname)) return script;

  purgeToolbarParams();
  return null;
}

export function stripToolbarHashParams(): void {
  try {
    const { hash, pathname, search } = window.location;
    if (!hash) return;

    const remaining = stripHash(hash);
    if (remaining === null) return;

    window.history.replaceState(
      window.history.state,
      "",
      `${pathname}${search}${remaining ? `#${remaining}` : ""}`,
    );
  } catch {
    // error should not break page load
  }
}

export function stripToolbarPayloadFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.hash) return url;

    const remaining = stripHash(parsed.hash);
    if (remaining === null) return url;

    parsed.hash = remaining ? `#${remaining}` : "";
    return parsed.toString();
  } catch {
    return url;
  }
}
